/* eslint-disable react/prop-types */
import React, { useEffect, useRef, useState, useMemo } from "react";

import {
	AlertDialog,
	AlertDialogBody,
	AlertDialogContent,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogOverlay,
	Box,
	Button,
	Divider,
	HStack,
	Icon,
	IconButton,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalHeader,
	ModalOverlay,
	Text,
	VStack,
	FormControl,
	FormLabel,
	Input,
	FormErrorMessage,
	Image,
	Textarea,
	Popover,
	PopoverTrigger,
	PopoverContent,
	PopoverCloseButton,
	useDisclosure,
	Tag,
	TagLabel,
	TagCloseButton,
	StackProps,
	useToast,
} from "@chakra-ui/react";
import { es } from "date-fns/locale";
import { Formik, Form, Field, FormikHelpers } from "formik";
import { Calendar } from "react-date-range";
import { FiEye, FiMail } from "react-icons/fi";
import * as Yup from "yup";

import { getComentarios, postComentario, putComentario } from "api/comentarios";
import {
	aprobarComunicacion,
	getComunicaciones,
	marcarComunicacionesVistas,
	postComunicacion,
	putComunicacion,
} from "api/comunicaciones";
import { TEXTOS_EVENTO } from "common/components/Comentarios";
import { ESTADO_COMUNICACION_CONFIG } from "common/components/EstadoComunicacionIndicator";
import FullsizeSpinner from "common/components/FullsizeSpinner";
import { EmailModal } from "components/ITE/components/EmailModal";
import { ITECommentInput, ITECommentOutput } from "components/ITE/types/comentarios";
import { ComunicacionType, ITEComunicacionInput, ITEComunicacionOutput } from "components/ITE/types/comunicaciones";
import { ActivoCompletoOutput } from "components/Portfolio/types/activos";
import { useAppSelector } from "store/store";

interface IProps {
	onOpenComunicaciones: () => void;
}

export const BotonComunicaciones: React.FC<IProps> = (props) => {
	const { onOpenComunicaciones } = props;

	return (
		<IconButton
			title="Histórico"
			aria-label="historico"
			colorScheme="gray"
			height="2rem"
			minW="1rem"
			style={{ aspectRatio: "1" }}
			onClick={(e) => {
				e.preventDefault();
				e.stopPropagation();
				onOpenComunicaciones();
			}}
		>
			<Icon as={FiEye} fontSize="1rem" color="gray.600" strokeWidth={"1"} />
		</IconButton>
	);
};

interface ModalComunicacionesProps {
	asset?: ActivoCompletoOutput;
	onClose: () => void;
	type: "eventos" | "comunicaciones";
	onComunicacionesActualizadas?: () => void;
}

export const ModalComunicaciones: React.FC<ModalComunicacionesProps> = (props) => {
	const { asset, type, onComunicacionesActualizadas } = props;

	const features = useAppSelector((state) => state.user.features.data);
	const email = useAppSelector((state) => state.user.userEmail);
	const [loadingComentarios, setLoadingComentarios] = useState(true);
	const [loadingComunicaciones, setLoadingComunicaciones] = useState(true);
	const [loading, setLoading] = useState(true);
	const [comentarios, setComentarios] = useState<ITECommentOutput[]>([]);
	const [comunicaciones, setComunicaciones] = useState<ITEComunicacionOutput[]>([]);
	const [items, setItems] = useState<
		Array<
			| { type: "comentario"; n: number; data: ITECommentOutput }
			| { type: "comunicacion"; n: number; data: ITEComunicacionOutput }
		>
	>([]);
	const [nuevoComentario, setNuevoComentario] = useState<ITECommentInput>();
	const [nuevaComunicacion, setNuevaComunicacion] = useState<ITEComunicacionInput>();
	const { isOpen: isEmailModalOpen, onOpen: onOpenEmailModal, onClose: onCloseEmailModal } = useDisclosure();

	const hasAddCommentFeature = features.indexOf("add-comment") >= 0;
	const isBasicaOnly = features.indexOf("manage-basica") >= 0;
	const canManageComunicacion = hasAddCommentFeature && !isBasicaOnly;

	const onEmailSent = () => {
		if (!asset?.id) return;
		getComunicaciones({ assetId: asset.id }).then((res) => setComunicaciones(res.data));
		onComunicacionesActualizadas?.();
	};

	useEffect(() => {
		if (!asset?.id) return;
		if (type === "eventos") {
			setLoadingComunicaciones(false);
			getComentarios({ assetId: asset.id })
				.then((res) => setComentarios(res.data))
				.then(() => setLoadingComentarios(false));
		} else {
			setLoadingComentarios(false);
			getComunicaciones({ assetId: asset.id })
				.then((res) => setComunicaciones(res.data))
				.then(() => setLoadingComunicaciones(false))
				.then(() => marcarComunicacionesVistas({ assetId: asset.id }))
				.then((res) => {
					if (!res.data.length) return;
					getComunicaciones({ assetId: asset.id }).then((refreshed) => setComunicaciones(refreshed.data));
					onComunicacionesActualizadas?.();
				});
		}
	}, [asset?.id, type]);

	useEffect(() => {
		if (loadingComentarios || loadingComunicaciones) return;
		const normalizedComentariosExtra = comentarios
			.filter((it) => it.event === "extra")
			.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
			.map((c, i) => ({ type: "comentario" as const, n: i + 1, data: c }));
		const normalizedComentarios = comentarios
			.filter((it) => it.event !== "extra")
			.map((c) => ({ type: "comentario" as const, n: 0, data: c }));
		const normalizedComunicaciones = comunicaciones
			.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
			.map((c, i) => ({ type: "comunicacion" as const, n: i + 1, data: c }));
		setItems(
			[...normalizedComentariosExtra, ...normalizedComentarios, ...normalizedComunicaciones].sort(
				(a, b) => new Date(b.data.created_at).getTime() - new Date(a.data.created_at).getTime(),
			),
		);
		setLoading(false);
	}, [comentarios, comunicaciones, loadingComentarios, loadingComunicaciones]);

	const onAddComentario = () => {
		if (!asset) return;
		setNuevoComentario({
			iteId: asset.ite_id,
			comment: "",
			event: "extra",
		});
	};

	const onClose = () => {
		setLoadingComentarios(true);
		setLoadingComunicaciones(true);
		setLoading(true);
		props.onClose();
	};

	return (
		<React.Fragment>
			<Modal isOpen={!!asset} onClose={onClose} size="6xl" closeOnEsc={false} closeOnOverlayClick={false}>
				<ModalOverlay />
				<ModalContent maxH="85vh" maxW="85vw">
					<ModalHeader>
						<HStack justifyContent="space-between" mr="2rem">
							<VStack align="start">
								<Text textStyle="body2" color="#006935">
									{type === "eventos" ? "Histórico de eventos" : "Histórico de comunicaciones"}
								</Text>
								<Text>{asset?.nombre}</Text>
							</VStack>
							<HStack gap="1.5rem">
								{type === "comunicaciones" && (
									<Button
										size="lg"
										bg="gray.400"
										fontWeight="normal"
										color="white"
										fontSize="small"
										w="12rem"
										leftIcon={<Icon as={FiMail} />}
										onClick={onOpenEmailModal}
										disabled={!canManageComunicacion}
									>
										Enviar email plataforma
									</Button>
								)}
								{type === "eventos" && (
									<Button
										size="lg"
										bg="gray.400"
										fontWeight="normal"
										color="white"
										fontSize="small"
										w="12rem"
										onClick={onAddComentario}
										disabled={!!nuevoComentario || !hasAddCommentFeature}
									>
										Añadir comentario
									</Button>
								)}
							</HStack>
						</HStack>
					</ModalHeader>
					<ModalCloseButton color="gray.500" />
					<Divider />
					<ModalBody overflowY="auto" overflowX="hidden" py="1rem">
						{loading ? (
							<FullsizeSpinner />
						) : (
							<>
								{nuevoComentario && !nuevaComunicacion && (
									<>
										<Comentario
											data={{
												...nuevoComentario,
												author: email,
												created_at: new Date().toISOString(),
												id: "",
												ite_id: asset?.ite_id || "",
											}}
											n={comentarios.filter((it) => it.event === "extra").length + 1}
											editable={hasAddCommentFeature}
											setComentarios={setComentarios}
											setNuevoComentario={setNuevoComentario}
											onDiscard={() => setNuevoComentario(undefined)}
										/>
										{items.length > 0 && <Divider my="1rem" />}
									</>
								)}
								{nuevaComunicacion && (
									<>
										<Comunicacion
											data={{
												...nuevaComunicacion,
												author: email,
												created_at: new Date().toISOString(),
												id: "",
												ite_id: asset?.ite_id || "",
											}}
											n={comunicaciones.length + 1}
											editable={hasAddCommentFeature}
											canManageComunicacion={canManageComunicacion}
											setComunicaciones={setComunicaciones}
											setNuevaComunicacion={setNuevaComunicacion}
											onDiscard={() => setNuevaComunicacion(undefined)}
											updatePortfolioData
											onComunicacionesActualizadas={onComunicacionesActualizadas}
										/>
										{items.length > 0 && <Divider my="1rem" />}
									</>
								)}
								{items.map((it, i) => {
									return (
										<React.Fragment key={it.data.id}>
											{i !== 0 && <Divider my="1rem" />}
											{it.type === "comentario" ? (
												<Comentario
													data={{
														...it.data,
														comment:
															it.data.event !== "extra"
																? `${TEXTOS_EVENTO[it.data.event]}${it.data.comment ? ` - ${it.data.comment}` : ""}`
																: it.data.comment,
													}}
													n={it.n}
													setComentarios={setComentarios}
													setNuevoComentario={setNuevoComentario}
													editable={it.data.event === "extra"}
												/>
											) : (
												<Comunicacion
													data={it.data}
													n={it.n}
													canManageComunicacion={canManageComunicacion}
													setComunicaciones={setComunicaciones}
													setNuevaComunicacion={setNuevaComunicacion}
													updatePortfolioData={items.filter((e) => e.type === "comunicacion")[0].data.id === it.data.id}
													onComunicacionesActualizadas={onComunicacionesActualizadas}
												/>
											)}
										</React.Fragment>
									);
								})}
							</>
						)}
					</ModalBody>
				</ModalContent>
			</Modal>
			{type === "comunicaciones" && (
				<EmailModal isOpen={isEmailModalOpen} onClose={onCloseEmailModal} iteId={asset?.ite_id} onSent={onEmailSent} />
			)}
		</React.Fragment>
	);
};

interface ComentarioProps {
	data: ITECommentOutput;
	n: number;
	setComentarios: React.Dispatch<React.SetStateAction<ITECommentOutput[]>>;
	setNuevoComentario: React.Dispatch<React.SetStateAction<ITECommentInput | undefined>>;
	editable?: boolean;
	onDiscard?: () => void;
}
interface ComentarioFormValues {
	comment: string;
}
const comentarioSchema = Yup.object({
	comment: Yup.string(),
});
const Comentario: React.FC<ComentarioProps> = (props) => {
	const { data, n, editable = true, setComentarios, setNuevoComentario, onDiscard } = props;
	const toast = useToast();
	const formatter = Intl.DateTimeFormat("es-ES", { year: "numeric", month: "2-digit", day: "2-digit" });
	const initialValues: ComentarioFormValues = {
		comment: data.comment || "",
	};
	const onSubmit = async (values: ComentarioFormValues, actions: FormikHelpers<ComentarioFormValues>) => {
		if (!editable) return;
		if (data.id) {
			try {
				const updatedComentario = await putComentario({ id: data.id, data: values });
				setComentarios((prevState) => prevState.map((e) => (e.id === data.id ? updatedComentario.data : e)));
				actions.resetForm({ values: { comment: updatedComentario.data.comment } });
			} catch (_) {
				toast({
					title: "Error al guardar comentario",
					status: "error",
					duration: 3000,
					isClosable: true,
				});
			}
		} else {
			try {
				const newComentario = await postComentario({ ...values, event: "extra", iteId: data.ite_id });
				setNuevoComentario(undefined);
				setComentarios((prevState) => [newComentario.data, ...prevState]);
			} catch (_) {
				toast({
					title: "Error al crear comentario",
					status: "error",
					duration: 3000,
					isClosable: true,
				});
			}
		}
	};
	return (
		<Formik initialValues={initialValues} validationSchema={comentarioSchema} onSubmit={onSubmit}>
			{({ handleSubmit, errors, touched, isSubmitting, dirty }) => (
				<Form onSubmit={handleSubmit} style={{ width: "100%" }}>
					<HStack w="100%" justifyContent="space-between" mb="1rem">
						<Text color="gray.800" fontWeight="700" fontSize="small">
							{TEXTOS_EVENTO[data.event]}
							{data.event === "extra" ? ` ${n}` : ""}
						</Text>
						{editable && (
							<HStack>
								{!data.id && (
									<Button
										py="0.25rem"
										variant="outline"
										color="gray.600"
										borderColor="gray.600"
										fontSize="small"
										isDisabled={isSubmitting}
										onClick={onDiscard}
									>
										Descartar
									</Button>
								)}
								<Button
									type="submit"
									py="0.25rem"
									variant="outline"
									color="gray.600"
									borderColor="gray.600"
									fontSize="small"
									isDisabled={isSubmitting || !dirty}
									isLoading={isSubmitting}
								>
									Guardar
								</Button>
							</HStack>
						)}
					</HStack>
					<VStack w="50%" alignItems="flex-start">
						<HStack w="100%" gap="1rem">
							<Box flex="1">
								<FormLabel>Autor</FormLabel>
								<Input isReadOnly value={data.author} />
							</Box>
							<Box flex="1">
								<FormLabel>Fecha</FormLabel>
								<Input isReadOnly value={formatter.format(new Date(data.created_at))} />
							</Box>
						</HStack>
						<Field name="comment">
							{({ field }) => (
								<FormControl isReadOnly={!editable} isInvalid={!!errors.comment && touched.comment}>
									<FormLabel htmlFor={field.name}>Comentario</FormLabel>
									<Textarea {...field} id={field.name} placeholder="Añadir comentario" />
									<FormErrorMessage>{errors.comment}</FormErrorMessage>
								</FormControl>
							)}
						</Field>
					</VStack>
				</Form>
			)}
		</Formik>
	);
};

interface ComunicacionProps {
	data: ITEComunicacionOutput;
	n: number;
	setComunicaciones: React.Dispatch<React.SetStateAction<ITEComunicacionOutput[]>>;
	setNuevaComunicacion: React.Dispatch<React.SetStateAction<ITEComunicacionInput | undefined>>;
	editable?: boolean;
	canManageComunicacion?: boolean;
	onDiscard?: () => void;
	updatePortfolioData?: boolean;
	onComunicacionesActualizadas?: () => void;
}
interface ComunicacionFormValues {
	comment: string;
	type: ComunicacionType;
	issuer?: string;
	date?: string;
	recipients?: string[];
	title?: string;
	comments?: string;
	response_issuer?: string[];
	response_date?: string;
	response_recipients?: string[];
	response_title?: string;
	response_comments?: string;
}
const comunicacionSchema = Yup.object({
	comment: Yup.string(),
	type: Yup.string().oneOf(["mail_manual", "mail_plataforma", "telefono"]),
	issuer: Yup.string(),
	date: Yup.string(),
	recipients: Yup.array().of(Yup.string()),
	title: Yup.string(),
	comments: Yup.string(),
	response_issuer: Yup.array().of(Yup.string()),
	response_date: Yup.string(),
	response_recipients: Yup.array().of(Yup.string()),
	response_title: Yup.string(),
	response_comments: Yup.string(),
});

const buildFormValues = (d: ITEComunicacionOutput | ITEComunicacionInput): ComunicacionFormValues => ({
	comment: d.comment || "",
	type: d.type || "mail_manual",
	issuer: d.issuer || "",
	date: d.date || "",
	recipients: d.recipients || [],
	title: d.title || "",
	comments: d.comments || "",
	response_issuer: d.response_issuer || [],
	response_date: d.response_date || "",
	response_recipients: d.response_recipients || [],
	response_title: d.response_title || "",
	response_comments: d.response_comments || "",
});

const Comunicacion: React.FC<ComunicacionProps> = (props) => {
	const {
		data,
		n,
		editable = true,
		canManageComunicacion = true,
		setComunicaciones,
		setNuevaComunicacion,
		onDiscard,
		updatePortfolioData,
		onComunicacionesActualizadas,
	} = props;
	const toast = useToast();
	const userEmail = useAppSelector((state) => state.user.userEmail);
	const isIssuer = !!data.issuer && data.issuer.trim().toLowerCase() === userEmail.trim().toLowerCase();
	const [isApproving, setIsApproving] = useState(false);
	const [isSavingComment, setIsSavingComment] = useState(false);
	const [isSavingResponse, setIsSavingResponse] = useState(false);
	const [isEditingResponse, setIsEditingResponse] = useState(!data.id);
	const [isEditingComment, setIsEditingComment] = useState(!data.id);
	const {
		isOpen: isConfirmFinalizarOpen,
		onOpen: onOpenConfirmFinalizar,
		onClose: onCloseConfirmFinalizar,
	} = useDisclosure();
	const cancelFinalizarRef = useRef<HTMLButtonElement>(null);
	const timestampFormatter = Intl.DateTimeFormat("es-ES", {
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
	});
	const initialValues: ComunicacionFormValues = {
		...buildFormValues(data),
		issuer: data.issuer || userEmail,
		response_issuer: data.response_issuer?.length ? data.response_issuer : !data.response_date ? [userEmail] : [],
	};
	const onSubmit = async (values: ComunicacionFormValues, actions: FormikHelpers<ComunicacionFormValues>) => {
		if (!editable) return;
		if (data.id) {
			try {
				const updatedComunicacion = await putComunicacion({ id: data.id, data: values });
				setComunicaciones((prevState) => prevState.map((e) => (e.id === data.id ? updatedComunicacion.data : e)));
				actions.resetForm({ values: buildFormValues(updatedComunicacion.data) });
				if (updatePortfolioData) {
					onComunicacionesActualizadas?.();
				}
			} catch (_) {
				toast({
					title: "Error al guardar comunicación",
					status: "error",
					duration: 3000,
					isClosable: true,
				});
			}
		} else {
			try {
				const newComunicacion = await postComunicacion({ ...values, ite_id: data.ite_id });
				setNuevaComunicacion(undefined);
				setComunicaciones((prevState) => [newComunicacion.data, ...prevState]);
				if (updatePortfolioData) {
					onComunicacionesActualizadas?.();
				}
			} catch (_) {
				toast({
					title: "Error al crear comnicación",
					status: "error",
					duration: 3000,
					isClosable: true,
				});
			}
		}
	};
	const onAprobar = async () => {
		if (!data.id) return;
		onCloseConfirmFinalizar();
		setIsApproving(true);
		try {
			const approved = await aprobarComunicacion({ id: data.id });
			setComunicaciones((prevState) => prevState.map((e) => (e.id === data.id ? approved.data : e)));
			if (updatePortfolioData) {
				onComunicacionesActualizadas?.();
			}
		} catch (_) {
			toast({
				title: "Error al finalizar comunicación",
				status: "error",
				duration: 3000,
				isClosable: true,
			});
		} finally {
			setIsApproving(false);
		}
	};
	return (
		<Formik initialValues={initialValues} validationSchema={comunicacionSchema} onSubmit={onSubmit}>
			{({ handleSubmit, errors, touched, isSubmitting, dirty, setFieldValue, resetForm, values }) => {
				const onGuardarComentario = async () => {
					if (!data.id || !isIssuer || !canManageComunicacion) return;
					setIsSavingComment(true);
					try {
						const updated = await putComunicacion({ id: data.id, data: values });
						setComunicaciones((prevState) => prevState.map((e) => (e.id === data.id ? updated.data : e)));
						resetForm({ values: buildFormValues(updated.data) });
						setIsEditingComment(false);
						if (updatePortfolioData) {
							onComunicacionesActualizadas?.();
						}
					} catch (_) {
						toast({
							title: "Error al guardar comentario",
							status: "error",
							duration: 3000,
							isClosable: true,
						});
					} finally {
						setIsSavingComment(false);
					}
				};
				const onEditarRespuesta = () => {
					const alreadyIncluded = (values.response_issuer ?? []).some(
						(e) => e.trim().toLowerCase() === userEmail.trim().toLowerCase(),
					);
					if (!alreadyIncluded) {
						setFieldValue("response_issuer", [...(values.response_issuer ?? []), userEmail]);
					}
					setIsEditingResponse(true);
				};
				const onEnviarRespuesta = async () => {
					if (!data.id) return;
					setIsSavingResponse(true);
					const alreadyIncluded = (values.response_issuer ?? []).some(
						(e) => e.trim().toLowerCase() === userEmail.trim().toLowerCase(),
					);
					const response_issuer = alreadyIncluded
						? values.response_issuer
						: [...(values.response_issuer ?? []), userEmail];
					try {
						const updated = await putComunicacion({
							id: data.id,
							data: { ...values, response_issuer, response_date: new Date().toISOString() },
						});
						setComunicaciones((prevState) => prevState.map((e) => (e.id === data.id ? updated.data : e)));
						resetForm({ values: buildFormValues(updated.data) });
						setIsEditingResponse(false);
						if (updatePortfolioData) {
							onComunicacionesActualizadas?.();
						}
					} catch (_) {
						toast({
							title: "Error al enviar la respuesta",
							status: "error",
							duration: 3000,
							isClosable: true,
						});
					} finally {
						setIsSavingResponse(false);
					}
				};
				const canEditTopFields = !data.id || data.type !== "mail_plataforma";
				const showTopSaveControls =
					editable && canEditTopFields && (!data.id || (!isEditingResponse && !isEditingComment));
				return (
					<Form onSubmit={handleSubmit} style={{ width: "100%" }}>
						<HStack w="100%" justifyContent="space-between" mb="1rem">
							<HStack spacing=".5rem">
								<Text color="gray.800" fontWeight="700" fontSize="small">{`Comunicación ${n}`}</Text>
								{data.seen_at && (
									<HStack spacing="0.25rem" color="gray.500">
										<Image src={ESTADO_COMUNICACION_CONFIG.vista.icon} alt="Vista" boxSize="0.9rem" />
										<Text fontSize="xs">
											Vista por {data.seen_by} el {timestampFormatter.format(new Date(data.seen_at))}
										</Text>
									</HStack>
								)}
							</HStack>
							{showTopSaveControls && (
								<HStack>
									{!data.id && (
										<Button
											py="0.25rem"
											variant="outline"
											color="gray.600"
											borderColor="gray.600"
											fontSize="small"
											isDisabled={isSubmitting}
											onClick={onDiscard}
										>
											Descartar
										</Button>
									)}
									<Button
										type="submit"
										py="0.25rem"
										variant="outline"
										color="gray.600"
										borderColor="gray.600"
										fontSize="small"
										isDisabled={isSubmitting || !dirty}
										isLoading={isSubmitting}
									>
										Guardar
									</Button>
								</HStack>
							)}
						</HStack>
						<HStack w="100%" gap="1rem" align="stretch">
							<VStack
								flex="4"
								alignItems="flex-start"
								p="1rem"
								borderRadius="0.5rem"
								borderColor="gray.200"
								borderWidth="1px"
								minW="0"
								display="flex"
							>
								<Text color="#006935" fontSize="small">
									{values.type === "telefono" ? "Llamada" : "Mail"}
								</Text>
								<Field name="issuer">
									{({ field }) => (
										<FormControl w="100%" isReadOnly isInvalid={!!errors.issuer && touched.issuer}>
											<FormLabel htmlFor={field.name}>Emisor</FormLabel>
											<Input {...field} id={field.name} placeholder="Emisor" />
											<FormErrorMessage>{errors.issuer}</FormErrorMessage>
										</FormControl>
									)}
								</Field>
								<Field name="recipients">
									{({ field }) => (
										<FormControl
											isReadOnly={!editable || data.type === "mail_plataforma"}
											isInvalid={!!errors.recipients && touched.recipients}
										>
											<FormLabel htmlFor={field.name}>Destinatarios</FormLabel>
											<TagInput
												value={field.value}
												onChange={(v) => setFieldValue(field.name, v)}
												isReadOnly={!editable || data.type === "mail_plataforma"}
												placeholder="Añadir destinatario"
											/>
											<FormErrorMessage>{errors.recipients}</FormErrorMessage>
										</FormControl>
									)}
								</Field>
								<Field name="title">
									{({ field }) => (
										<FormControl
											isReadOnly={!editable || data.type === "mail_plataforma"}
											isInvalid={!!errors.title && touched.title}
										>
											<FormLabel htmlFor={field.name}>Título</FormLabel>
											<Input {...field} id={field.name} placeholder="Título" />
											<FormErrorMessage>{errors.title}</FormErrorMessage>
										</FormControl>
									)}
								</Field>
								<Field name="comments">
									{({ field }) => (
										<FormControl
											isReadOnly={!editable || data.type === "mail_plataforma"}
											isInvalid={!!errors.comments && touched.comments}
											flex="1"
											display="flex"
											flexDirection="column"
										>
											<FormLabel htmlFor={field.name}>
												{values.type === "telefono" ? "Comentarios de la llamada" : "Cuerpo"}
											</FormLabel>
											<Textarea
												{...field}
												id={field.name}
												placeholder="Añadir comentario"
												flex="1 1 auto"
												minH="6rem"
												resize="vertical"
											/>
											<FormErrorMessage>{errors.comments}</FormErrorMessage>
										</FormControl>
									)}
								</Field>
								{editable && data.type !== "mail_plataforma" ? (
									<Field name="date">
										{({ field }) => (
											<FormControl w="100%" isInvalid={!!errors.date && touched.date}>
												<FormLabel htmlFor={field.name}>Fecha</FormLabel>
												<CalendarInput
													value={field.value ? new Date(field.value) : undefined}
													onChange={(date) => setFieldValue(field.name, date.toISOString())}
													placeholder="Fecha"
												/>
												<FormErrorMessage>{errors.date}</FormErrorMessage>
											</FormControl>
										)}
									</Field>
								) : (
									data.date && (
										<HStack spacing="0.25rem" color="gray.500" pt="0.5rem">
											<Image src={ESTADO_COMUNICACION_CONFIG.enviada.icon} alt="Enviada" boxSize="0.9rem" />
											<Text textStyle="body3" fontSize="xs">
												Enviado el {timestampFormatter.format(new Date(data.date))}
											</Text>
										</HStack>
									)
								)}
							</VStack>

							<VStack
								flex="4"
								alignItems="flex-start"
								p="1rem"
								borderRadius="0.5rem"
								borderColor="gray.200"
								borderWidth="1px"
								minW="0"
								display="flex"
							>
								<HStack w="100%" justifyContent="space-between">
									<Text color="#006935" fontSize="small">
										{"Respuesta"}
									</Text>
									{data.id && editable && !data.revised_at && !isEditingResponse && (
										<Button
											py="0.25rem"
											variant="outline"
											color="gray.600"
											borderColor="gray.600"
											fontSize="small"
											onClick={onEditarRespuesta}
										>
											{data.response_date ? "Editar" : "Responder"}
										</Button>
									)}
								</HStack>
								<Field name="response_issuer">
									{({ field }) => (
										<FormControl isInvalid={!!errors.response_issuer && touched.response_issuer}>
											<FormLabel htmlFor={field.name}>Emisor</FormLabel>
											<TagInput
												value={field.value ?? []}
												onChange={(v) => setFieldValue(field.name, v)}
												isReadOnly={!editable || !!data.revised_at || !isEditingResponse}
												lockedValues={[userEmail]}
												placeholder="Añadir emisor"
											/>
											<FormErrorMessage>{errors.response_issuer}</FormErrorMessage>
										</FormControl>
									)}
								</Field>
								<Field name="response_title">
									{({ field }) => (
										<FormControl
											isReadOnly={!editable || !!data.revised_at || !isEditingResponse}
											isInvalid={!!errors.response_title && touched.response_title}
										>
											<FormLabel htmlFor={field.name}>Título</FormLabel>
											<Input {...field} id={field.name} placeholder="Título" />
											<FormErrorMessage>{errors.response_title}</FormErrorMessage>
										</FormControl>
									)}
								</Field>
								<Field name="response_comments">
									{({ field }) => (
										<FormControl
											isReadOnly={!editable || !!data.revised_at || !isEditingResponse}
											isInvalid={!!errors.response_comments && touched.response_comments}
											flex="1"
											display="flex"
											flexDirection="column"
										>
											<FormLabel htmlFor={field.name}>
												{values.type === "telefono" ? "Comentarios de la llamada" : "Cuerpo"}
											</FormLabel>
											<Textarea
												{...field}
												id={field.name}
												placeholder="Añadir comentario"
												flex="1 1 auto"
												minH="6rem"
												resize="vertical"
											/>
											<FormErrorMessage>{errors.response_comments}</FormErrorMessage>
										</FormControl>
									)}
								</Field>
								{data.id && editable && (
									<>
										{data.response_date && !isEditingResponse && (
											<HStack spacing="0.25rem" color="gray.500" pt="0.5rem">
												<Image src={ESTADO_COMUNICACION_CONFIG.contestada.icon} alt="Respondida" boxSize="0.9rem" />
												<Text textStyle="body3" fontSize="xs">
													Respondida por {(data.response_issuer ?? []).join(", ")} el{" "}
													{timestampFormatter.format(new Date(data.response_date))}
												</Text>
											</HStack>
										)}
										{isEditingResponse && !data.revised_at && (
											<Button
												w="100%"
												bg="#006935"
												color="white"
												_hover={{ bg: "green.700" }}
												isLoading={isSavingResponse}
												onClick={onEnviarRespuesta}
											>
												{data.response_date ? "Guardar respuesta" : "Enviar respuesta"}
											</Button>
										)}
									</>
								)}
							</VStack>

							<VStack flex="3" alignItems="flex-start" minW="0" display="flex">
								<Field name="comment">
									{({ field }) => (
										<FormControl
											isReadOnly={!editable || !isIssuer || !canManageComunicacion || !isEditingComment}
											isInvalid={!!errors.comment && touched.comment}
											flex="1"
											display="flex"
											flexDirection="column"
										>
											<HStack w="100%" justifyContent="space-between" mb="0.5rem">
												<FormLabel htmlFor={field.name} mb="0">
													Comentario
												</FormLabel>
												{data.id && editable && isIssuer && canManageComunicacion && !isEditingComment && (
													<Button
														py="0.25rem"
														variant="outline"
														color="gray.600"
														borderColor="gray.600"
														fontSize="small"
														onClick={() => setIsEditingComment(true)}
													>
														Editar
													</Button>
												)}
											</HStack>
											<Textarea
												{...field}
												id={field.name}
												placeholder="Añadir comentario"
												flex="1 1 auto"
												minH="6rem"
												resize="vertical"
											/>
											<FormErrorMessage>{errors.comment}</FormErrorMessage>
										</FormControl>
									)}
								</Field>
								{data.id && editable && isIssuer && canManageComunicacion && isEditingComment && (
									<Button
										py="0.25rem"
										w="100%"
										variant="outline"
										color="gray.600"
										borderColor="gray.600"
										fontSize="small"
										isLoading={isSavingComment}
										isDisabled={!dirty}
										onClick={onGuardarComentario}
									>
										Guardar comentario
									</Button>
								)}
								{data.id &&
									(data.revised_at ? (
										<VStack w="100%" justifyContent="center" py="0.5rem" spacing="0">
											<HStack spacing="0.25rem" color="green.600">
												<Image src={ESTADO_COMUNICACION_CONFIG.aprobada.icon} alt="Aprobada" boxSize="0.9rem" />
												<Text textStyle="body3" fontWeight="bold">
													Finalizada
												</Text>
											</HStack>
											<Text textStyle="body3" color="gray.500" fontSize="xs">
												{data.revised_by ? `por ${data.revised_by} ` : ""}
												el {timestampFormatter.format(new Date(data.revised_at))}
											</Text>
										</VStack>
									) : (
										editable &&
										canManageComunicacion && (
											<Button
												w="100%"
												bg="green.500"
												color="white"
												_hover={{ bg: "green.600" }}
												isLoading={isApproving}
												onClick={onOpenConfirmFinalizar}
											>
												Finalizar
											</Button>
										)
									))}
							</VStack>
						</HStack>
						<AlertDialog
							isOpen={isConfirmFinalizarOpen}
							leastDestructiveRef={cancelFinalizarRef}
							onClose={onCloseConfirmFinalizar}
						>
							<AlertDialogOverlay>
								<AlertDialogContent>
									<AlertDialogHeader fontSize="lg" fontWeight="bold">
										Finalizar comunicación
									</AlertDialogHeader>
									<AlertDialogBody>
										¿Seguro que quieres finalizar esta comunicación? Una vez finalizada no se podrá editar la respuesta.
									</AlertDialogBody>
									<AlertDialogFooter>
										<Button ref={cancelFinalizarRef} onClick={onCloseConfirmFinalizar}>
											Cancelar
										</Button>
										<Button colorScheme="green" onClick={onAprobar} isLoading={isApproving} ml={3}>
											Finalizar
										</Button>
									</AlertDialogFooter>
								</AlertDialogContent>
							</AlertDialogOverlay>
						</AlertDialog>
					</Form>
				);
			}}
		</Formik>
	);
};

interface CalendarProps {
	value: Date | undefined;
	onChange?: (date: Date) => void;
	isReadOnly?: boolean;
	placeholder?: string;
}
const CalendarInput: React.FC<CalendarProps> = (props) => {
	const { value, onChange, isReadOnly, placeholder } = props;
	const { onToggle, isOpen, onClose } = useDisclosure();
	const formatter = Intl.DateTimeFormat("es-ES", {
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
	});
	return (
		<Popover
			returnFocusOnClose={false}
			isOpen={isOpen && !isReadOnly}
			onClose={onClose}
			placement="auto"
			closeOnBlur={true}
			isLazy
		>
			<PopoverTrigger>
				<Input
					onClick={onToggle}
					value={value ? formatter.format(value) : ""}
					onChange={console.log}
					cursor={isReadOnly ? undefined : "pointer"}
					isReadOnly={isReadOnly}
					placeholder={placeholder}
				/>
			</PopoverTrigger>
			<PopoverContent w="100%" h="100%">
				<PopoverCloseButton />
				<Calendar
					date={value}
					onChange={(date) => {
						onChange?.(date);
						onToggle();
					}}
					locale={es}
				/>
			</PopoverContent>
		</Popover>
	);
};

interface TagInputProps {
	value: string[];
	onChange?: (v: string[]) => void;
	isReadOnly?: boolean;
	placeholder?: string;
	lockedValues?: string[];
}
const TagInput: React.FC<TagInputProps> = (props) => {
	const { value, onChange, isReadOnly, placeholder, lockedValues = [] } = props;
	const [inputValue, setInputValue] = useState<string>("");
	const [isFocused, setIsFocused] = useState<boolean>(false);
	const showBox = !isReadOnly || value.length > 0;
	const styles: StackProps = {
		maxWidth: "100%",
		...(showBox && {
			border: "1px solid",
			borderColor: "gray.200",
			borderRadius: "0.375rem",
		}),
		flexWrap: "wrap",
		...(value.length && { padding: "0.5rem" }),
	};
	const isLocked = (tag: string) => lockedValues.some((v) => v.trim().toLowerCase() === tag.trim().toLowerCase());
	const renderTags = useMemo(
		() =>
			value.map((tag, index) => (
				<Tag key={index} marginInlineStart="0 !important" marginInlineEnd="0.5rem !important" mt="0.5rem !important">
					<TagLabel color="gray.500">{tag}</TagLabel>
					{!isReadOnly && !isLocked(tag) && <TagCloseButton onClick={() => removeTag(index)} />}
				</Tag>
			)),
		[value, isReadOnly, lockedValues],
	);
	const removeTag = (index: number) => {
		const newTags = [...value];
		newTags.splice(index, 1);
		onChange?.(newTags);
		setInputValue("");
	};
	const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setInputValue(e.target.value);
	};
	const handleOnKeyDown = (e) => {
		if (e.key === "Enter" && isFocused) {
			e.preventDefault();
		}
		if (["Enter", "Tab", ","].includes(e.key) && inputValue) {
			e.preventDefault();
			if (value.includes(inputValue)) return;
			onChange?.([...value, inputValue]);
			setInputValue("");
		}
	};
	const handleOnFocus = () => {
		setIsFocused(true);
	};

	const handleOnBlur = () => {
		setIsFocused(false);
		if (inputValue) {
			if (value.includes(inputValue)) return;
			onChange?.([...value, inputValue]);
			setInputValue("");
		}
	};
	return (
		<HStack {...styles}>
			{!isReadOnly && (
				<Input
					placeholder={placeholder}
					margin="0"
					flex={1}
					px="1rem"
					minWidth="100%"
					border="none"
					onChange={handleOnChange}
					onKeyDown={handleOnKeyDown}
					onFocus={handleOnFocus}
					onBlur={handleOnBlur}
					value={inputValue}
				/>
			)}
			{renderTags}
		</HStack>
	);
};
